import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState, useMemo, useRef } from 'react'
import { jobSchema, type JobFormData } from '../schemas/jobSchemas'
import { Job, RecurrenceFrequency, JobBreak, JobAssignment } from '../types/job'
import { PayChangeEffectiveDateModal } from '@/features/jobLogs/components/PayChangeEffectiveDateModal'

function hasPayChangeWithTimeEntries(
  oldAssignments: JobAssignment[] | string | undefined,
  newAssignments: JobAssignment[] | null,
  timeEntries: { userId?: string }[] | undefined
): boolean {
  if (!newAssignments || newAssignments.length === 0) return false
  const entriesByUser = new Set((timeEntries ?? []).map(e => e.userId).filter(Boolean))
  if (entriesByUser.size === 0) return false
  const oldByUser = new Map<string, JobAssignment>()
  const oldArr = Array.isArray(oldAssignments) && oldAssignments.length > 0 && typeof oldAssignments[0] === 'object'
    ? (oldAssignments as JobAssignment[])
    : []
  for (const a of oldArr) {
    if (a?.userId) oldByUser.set(a.userId, a)
  }
  for (const newA of newAssignments) {
    const userId = newA?.userId
    if (!userId || !entriesByUser.has(userId)) continue
    const oldA = oldByUser.get(userId)
    const oldRate = oldA?.payType === 'hourly' ? oldA.hourlyRate : oldA?.price
    const newRate = newA?.payType === 'hourly' ? newA.hourlyRate : newA?.price
    if (oldRate !== newRate) return true
  }
  return false
}
import {
  Alert,
  AlertIcon,
  AppButton,
  CheckboxField,
  DateField,
  PlusIcon,
  SearchableSelectField,
  SelectField,
  TextAreaField,
  TextField,
  TimePickerField,
  TrashIcon,
  labelCls,
} from './schedulingUi'
import { useContactStore } from '@/features/crm/store/contactStore'
import { useServiceStore } from '../store/serviceStore'
import { useQuoteStore } from '@/features/quotes/store/quoteStore'
import { useInvoiceStore } from '@/features/invoices/store/invoiceStore'
import { useAuthStore } from '@/features/auth/store/authStore'
import { useJobStore } from '../store/jobStore'
import { services as apiServices } from '@/lib/api/services'
import { format, addWeeks, addMonths } from 'date-fns'
import { cn } from '@/lib/utils'
import { parseDateStringLocal } from '@/lib/utils/dateUtils'
import { getTeamAssignmentRoleValidationMessage } from '@/lib/utils/assignmentRoleValidation'

interface JobFormProps {
  job?: Job
  onSubmit: (data: JobFormData, existingJobId?: string, isIndependent?: boolean) => Promise<void>
  onCancel: () => void
  isLoading?: boolean
  defaultContactId?: string
  defaultTitle?: string
  defaultNotes?: string
  defaultLocation?: string
  defaultServiceId?: string
  defaultDescription?: string
  defaultPrice?: number
  defaultAssignedTo?: Array<{
    userId: string
    roleId?: string
    role: string
    price?: number | null
    payType?: 'job' | 'hourly'
    hourlyRate?: number | null
  }>
  initialQuoteId?: string
  initialInvoiceId?: string
  error?: string | null
  schedulingUnscheduledJob?: boolean
  isSimpleCreate?: boolean // When true and not editing, show only title, description, location, contact
  allowLinkExistingJob?: boolean // When true, show option to link to existing job
  existingJobId?: string // Pre-selected existing job ID
  timeEntries?: Array<{ userId?: string }> // When provided and pay changes, show effective-date modal
  defaultStatus?: Job['status'] // Pre-select the status on a NEW job (e.g. 'pending-confirmation' for unconfirmed scheduling)
}

interface TeamMemberOption {
  id: string
  name: string
}

const JobForm = ({
  job,
  onSubmit,
  onCancel,
  isLoading,
  defaultContactId,
  defaultTitle,
  defaultNotes,
  defaultLocation,
  defaultServiceId,
  defaultDescription,
  defaultPrice,
  defaultAssignedTo,
  initialQuoteId,
  initialInvoiceId,
  error,
  schedulingUnscheduledJob,
  isSimpleCreate = false,
  allowLinkExistingJob = false,
  existingJobId: propExistingJobId,
  timeEntries: propTimeEntries,
  defaultStatus,
}: JobFormProps) => {
  const { contacts, fetchContacts } = useContactStore()
  const { services, fetchServices } = useServiceStore()
  const { quotes, fetchQuotes } = useQuoteStore()
  const { invoices, fetchInvoices } = useInvoiceStore()
  const { user } = useAuthStore()
  const { jobs: allJobs, jobsForLink, fetchJobs, fetchJobsForLink } = useJobStore()
  const canSchedule = user?.canScheduleAppointments !== false
  const canCreateJobs = user?.canCreateJobs !== false
  const isAdminOrOwner = user?.role === 'admin' || user?.role === 'owner'
  const canSeeOtherJobs = isAdminOrOwner || (user?.canSeeOtherJobs ?? true)
  // Employees can see job prices only if permission is enabled
  // But they can always see their own assignment pay
  const canSeeJobPrices = isAdminOrOwner || (user?.canSeeJobPrices ?? true)
  const currentUserId = user?.id
  const [teamMembers, setTeamMembers] = useState<TeamMemberOption[]>([])
  const [jobRoles, setJobRoles] = useState<Array<{ id: string; title: string }>>([])
  const [canShowAssignee, setCanShowAssignee] = useState(false)
  const [assignments, setAssignments] = useState<JobAssignment[]>([])
  const [jobSelectionMode, setJobSelectionMode] = useState<'new' | 'existing' | 'independent'>(
    propExistingJobId ? 'existing' : 'new'
  )
  const [selectedExistingJobId, setSelectedExistingJobId] = useState<string | undefined>(
    propExistingJobId
  )
  const [availableJobs, setAvailableJobs] = useState<Job[]>([])
  const [startDate, setStartDate] = useState(
    job && job.startTime ? format(new Date(job.startTime), 'yyyy-MM-dd') : ''
  )
  const [scheduleDateError, setScheduleDateError] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [showPayChangeModal, setShowPayChangeModal] = useState(false)
  const [pendingSubmit, setPendingSubmit] = useState<{
    formData: any
    existingJobId?: string
    isIndependent?: boolean
  } | null>(null)
  const [startTime, setStartTime] = useState(
    job && job.startTime ? format(new Date(job.startTime), 'HH:mm') : '09:00'
  )
  const [isAllDay, setIsAllDay] = useState(false)

  /**
   * Whether the chosen start is already in the past. All-day jobs compare by date only, so
   * "today" is never flagged; timed jobs compare the actual start instant.
   */
  const isStartInPast = (() => {
    if (!startDate) return false
    if (isAllDay) {
      const day = parseDateStringLocal(startDate)
      if (!day) return false
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return day < today
    }
    const start = new Date(`${startDate}T${startTime || '09:00'}`)
    return !isNaN(start.getTime()) && start < new Date()
  })()

  const [toBeScheduled, setToBeScheduled] = useState(() => {
    // If user can't schedule, always default to toBeScheduled = true
    if (!canSchedule) return true
    // If in simplified create mode, default to toBeScheduled = true
    if (isSimpleCreate && !job) return true
    return job?.toBeScheduled || false
  })
  const [repeatPattern, setRepeatPattern] = useState<string>('none')
  // 'never' = truly open-ended: no count, no end date. The backend materializes a rolling
  // 12-month window and the recurrence-topup Lambda extends it, so the series keeps going
  // until the user deletes it.
  const [endRepeatMode, setEndRepeatMode] = useState<'never' | 'after' | 'on-date'>('never')
  const [endRepeatDate, setEndRepeatDate] = useState<string>('')
  const [occurrenceCount, setOccurrenceCount] = useState<number>(12)
  const [customDays, setCustomDays] = useState<number[]>([])

  // Duration unit and value state
  const inferDurationUnit = (
    startTime: string,
    endTime: string
  ): { unit: 'minutes' | 'hours' | 'days' | 'weeks'; value: number } => {
    const durationMs = new Date(endTime).getTime() - new Date(startTime).getTime()
    const durationMinutes = Math.round(durationMs / 60000)
    const durationHours = durationMinutes / 60
    const durationDays = durationMinutes / (24 * 60)

    if (durationMinutes < 60) {
      return { unit: 'minutes', value: durationMinutes }
    } else if (durationHours < 24) {
      // Use decimal hours when not a whole number (e.g. 1.5 not 2) to preserve user's intent
      const value = durationHours % 1 === 0 ? durationHours : Math.round(durationHours * 10) / 10
      return { unit: 'hours', value }
    } else if (durationDays < 14) {
      return { unit: 'days', value: Math.ceil(durationDays) }
    } else {
      return { unit: 'weeks', value: Math.ceil(durationDays / 7) }
    }
  }

  const initialDuration =
    job && job.startTime && job.endTime
      ? inferDurationUnit(job.startTime, job.endTime)
      : { unit: 'minutes' as const, value: 60 }
  const [durationUnit, setDurationUnit] = useState<'minutes' | 'hours' | 'days' | 'weeks'>(
    initialDuration.unit
  )
  const [durationValue, setDurationValue] = useState<number>(initialDuration.value)

  // Remembers the duration/start-time from before "All Day" was toggled on. All-day forces the
  // unit to 'days' (which hides the Start Time field), so unchecking must restore the prior
  // settings — otherwise the unit stays 'days' and Start Time never comes back.
  const preAllDayDurationRef = useRef<{
    unit: 'minutes' | 'hours' | 'days' | 'weeks'
    value: number
    startTime: string
  }>({ unit: initialDuration.unit, value: initialDuration.value, startTime: startTime })

  // Handle duration unit changes - keeps the entered value
  const handleDurationUnitChange = (newUnit: 'minutes' | 'hours' | 'days' | 'weeks') => {
    setDurationUnit(newUnit)
  }

  // Job breaks state
  const [breaks, setBreaks] = useState<JobBreak[]>(job?.breaks || [])
  const [showBreaks, setShowBreaks] = useState(false)

  // Job source selection state
  const [selectedSource, setSelectedSource] = useState<{
    type: 'none' | 'quote' | 'invoice'
    id?: string
  }>({
    type: initialQuoteId
      ? 'quote'
      : initialInvoiceId
        ? 'invoice'
        : job?.quoteId
          ? 'quote'
          : job?.invoiceId
            ? 'invoice'
            : 'none',
    id: initialQuoteId || initialInvoiceId || job?.quoteId || job?.invoiceId,
  })

  // Title is always a text field - no dropdown

  // Location state
  const [locationMode, setLocationMode] = useState<'contact' | 'custom'>('contact')
  const [customLocation, setCustomLocation] = useState('')

  useEffect(() => {
    fetchContacts()
    fetchServices()
    // Lazily fetch quotes and invoices if not already loaded
    if (quotes.length === 0) {
      fetchQuotes()
    }
    if (invoices.length === 0) {
      fetchInvoices()
    }
  }, [fetchContacts, fetchServices, fetchQuotes, fetchInvoices, quotes.length, invoices.length])

  // Update selection mode when existingJobId prop changes
  useEffect(() => {
    if (propExistingJobId) {
      setJobSelectionMode('existing')
      setSelectedExistingJobId(propExistingJobId)
    }
  }, [propExistingJobId])

  // Fetch jobs for "link to existing job" selector - includes ALL jobs (even those without bookings)
  useEffect(() => {
    if (allowLinkExistingJob && canSeeOtherJobs && !schedulingUnscheduledJob && !job) {
      const startDate = new Date()
      startDate.setFullYear(startDate.getFullYear() - 10)
      const endDate = new Date()
      endDate.setFullYear(endDate.getFullYear() + 10)
      fetchJobsForLink(startDate, endDate)
    }
  }, [allowLinkExistingJob, canSeeOtherJobs, schedulingUnscheduledJob, job, fetchJobsForLink])

  // Filter available jobs based on contact (use jobsForLink when linking - includes unlinked jobs)
  const filteredJobs = useMemo(() => {
    if (!allowLinkExistingJob || !canSeeOtherJobs) return []
    const source = allowLinkExistingJob ? jobsForLink : allJobs
    const filtered = source.filter(j => {
      // Exclude archived/deleted jobs
      if (j.archivedAt || j.deletedAt) return false
      // Exclude the current job if editing
      if (job && j.id === job.id) return false
      // Filter by contact if defaultContactId is set
      if (defaultContactId && j.contactId !== defaultContactId) return false
      return true
    })
    // Deduplicate by job id - jobs with multiple bookings can appear multiple times from the API
    const seen = new Set<string>()
    const deduped = filtered.filter(j => {
      if (seen.has(j.id)) return false
      seen.add(j.id)
      return true
    })
    return deduped.slice(0, 100) // Limit to 100 results (SearchableSelect will handle search filtering)
  }, [allJobs, jobsForLink, defaultContactId, allowLinkExistingJob, canSeeOtherJobs, job])

  useEffect(() => {
    const role = user?.role
    const canAssign = role !== 'employee'
    if (!canAssign) {
      setCanShowAssignee(false)
      return
    }
    const load = async () => {
      try {
        const [usersData, billingData, rolesData] = await Promise.all([
          apiServices.users.getAll(),
          apiServices.billing.getStatus(),
          apiServices.jobRoles.getAll().catch(() => []), // Gracefully handle if job roles don't exist yet
        ])
        const hasTeamTier =
          !!billingData?.canInviteTeamMembers ||
          billingData?.subscriptionTier === 'team' ||
          billingData?.subscriptionTier === 'team-plus'
        setCanShowAssignee(hasTeamTier)
        if (hasTeamTier) {
          setTeamMembers(
            (usersData || []).map((m: { id: string; name: string; email?: string }) => ({
              id: m.id,
              name: m.name || m.email || 'Unknown',
            }))
          )
        }
        if (Array.isArray(rolesData)) {
          setJobRoles(rolesData.map((r: { id: string; title: string }) => ({ id: r.id, title: r.title })))
        }
      } catch {
        setCanShowAssignee(false)
      }
    }
    load()
  }, [user?.role])

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    watch,
    setValue,
  } = useForm<JobFormData>({
    resolver: zodResolver(jobSchema),
    defaultValues: {
      title: job?.title || defaultTitle || '',
      description: job?.description || defaultDescription || '',
      contactId: job?.contactId || defaultContactId || '',
      serviceId: job?.serviceId || defaultServiceId || '',
      quoteId: job?.quoteId || initialQuoteId || '',
      invoiceId: job?.invoiceId || initialInvoiceId || '',
      startTime: job?.startTime || '',
      endTime: job?.endTime || '',
      status: job?.status || defaultStatus || 'active',
      location: job?.location || defaultLocation || '',
      price: job?.price != null ? job.price.toString() : (defaultPrice != null ? defaultPrice.toString() : ''),
      notes: job?.notes || defaultNotes || '',
      _jobSelectionMode: (propExistingJobId ? 'existing' : 'new') as 'new' | 'existing' | 'independent',
      assignedTo: (() => {
        // If editing a job, use job's assignedTo
        if (job?.assignedTo) {
          // Handle both old format (string/string[]) and new format (JobAssignment[])
          if (Array.isArray(job.assignedTo)) {
            // Check if it's new format (objects with userId)
            if (
              job.assignedTo.length > 0 &&
              typeof job.assignedTo[0] === 'object' &&
              'userId' in job.assignedTo[0]
            ) {
              return job.assignedTo as JobAssignment[]
            }
            // Old format: array of strings
            return (job.assignedTo as string[]).map(id => ({
              userId: id,
              role: 'Team Member',
              price: null,
              payType: 'job' as const,
              hourlyRate: null,
            }))
          }
          // Old format: single string
          return [{ userId: job.assignedTo as string, role: 'Team Member', price: null, payType: 'job' as const, hourlyRate: null }]
        }
        // Use defaultAssignedTo if provided
        return defaultAssignedTo || []
      })(),
    },
  })

  const selectedServiceId = watch('serviceId')
  const selectedContactId = watch('contactId')

  // Reset form when defaults change (e.g., when opening from job log detail)
  useEffect(() => {
    if (!job && (defaultTitle || defaultContactId || defaultPrice != null || defaultServiceId || defaultDescription || defaultLocation || defaultNotes || defaultAssignedTo)) {
      // Set assignments state if defaultAssignedTo is provided
      if (defaultAssignedTo && defaultAssignedTo.length > 0) {
        setAssignments(defaultAssignedTo)
      }
      
      reset({
        title: defaultTitle || '',
        description: defaultDescription || '',
        contactId: defaultContactId || '',
        serviceId: defaultServiceId || '',
        quoteId: initialQuoteId || '',
        invoiceId: initialInvoiceId || '',
        startTime: '',
        endTime: '',
        status: defaultStatus || 'active',
        location: defaultLocation || '',
        price: defaultPrice != null ? defaultPrice.toString() : '',
        notes: defaultNotes || '',
        assignedTo: defaultAssignedTo || [],
      })
    }
  }, [defaultTitle, defaultContactId, defaultPrice, defaultServiceId, defaultDescription, defaultLocation, defaultNotes, defaultAssignedTo, initialQuoteId, initialInvoiceId, job, reset, defaultStatus])

  // Set price from quote/invoice when initialQuoteId/initialInvoiceId is provided and quote/invoice loads
  // Only set if price is empty or matches defaultPrice (user hasn't manually changed it)
  useEffect(() => {
    if (initialQuoteId && quotes.length > 0 && !job) {
      const quote = quotes.find(q => q.id === initialQuoteId)
      const currentPrice = watch('price')
      if (quote && (currentPrice === '' || currentPrice === defaultPrice?.toString() || currentPrice === '0')) {
        setValue('price', quote.total.toString())
      }
    }
    if (initialInvoiceId && invoices.length > 0 && !job) {
      const invoice = invoices.find(i => i.id === initialInvoiceId)
      const currentPrice = watch('price')
      if (invoice && (currentPrice === '' || currentPrice === defaultPrice?.toString() || currentPrice === '0')) {
        setValue('price', invoice.total.toString())
      }
    }
  }, [initialQuoteId, initialInvoiceId, quotes, invoices, defaultPrice, job, setValue, watch])

  // Auto-populate location when contact changes
  useEffect(() => {
    if (locationMode === 'contact' && selectedContactId) {
      const contact = contacts.find(c => c.id === selectedContactId)
      if (contact && contact.address) {
        const fullAddress = [contact.address, contact.city, contact.state, contact.zipCode]
          .filter(Boolean)
          .join(', ')
        setValue('location', fullAddress)
      } else {
        setValue('location', '')
      }
    }
  }, [selectedContactId, locationMode, contacts, setValue])

  // Auto-populate fields when job source is selected
  useEffect(() => {
    if (selectedSource.type === 'quote' && selectedSource.id) {
      const quote = quotes.find(q => q.id === selectedSource.id)
      if (quote) {
        setValue('contactId', quote.contactId)
        setValue('quoteId', quote.id)
        setValue('invoiceId', '')
        setValue('price', quote.total.toString())
      }
    } else if (selectedSource.type === 'invoice' && selectedSource.id) {
      const invoice = invoices.find(i => i.id === selectedSource.id)
      if (invoice) {
        setValue('contactId', invoice.contactId)
        setValue('invoiceId', invoice.id)
        setValue('quoteId', '')
        setValue('price', invoice.total.toString())
      }
    } else if (selectedSource.type === 'none') {
      setValue('quoteId', '')
      setValue('invoiceId', '')
      // Reset to defaults if switching back to custom job
      if (!job) {
        setValue('contactId', defaultContactId || '')
        setValue('notes', defaultNotes || '')
        setValue('price', defaultPrice != null ? defaultPrice.toString() : '')
      }
    }
  }, [
    selectedSource,
    quotes,
    invoices,
    setValue,
    defaultContactId,
    defaultTitle,
    defaultNotes,
    defaultPrice,
    job,
  ])

  useEffect(() => {
    if (selectedServiceId) {
      const service = services.find(s => s.id === selectedServiceId)
      if (service) {
        setDurationUnit('minutes')
        setDurationValue(service.duration)
      }
    }
  }, [selectedServiceId, services])

  // Initialize assignments from job
  useEffect(() => {
    if (job?.assignedTo) {
      if (Array.isArray(job.assignedTo)) {
        if (
          job.assignedTo.length > 0 &&
          typeof job.assignedTo[0] === 'object' &&
          'userId' in job.assignedTo[0]
        ) {
          // Ensure payType and hourlyRate are set for all assignments
          setAssignments((job.assignedTo as JobAssignment[]).map(assignment => ({
            ...assignment,
            payType: assignment.payType || 'job',
            hourlyRate: assignment.hourlyRate ?? null,
            price: assignment.price ?? null,
          })))
        } else {
          setAssignments(
            (job.assignedTo as string[]).map(id => ({
              userId: id,
              role: 'Team Member',
              price: null,
              payType: 'job' as const,
              hourlyRate: null,
            }))
          )
        }
      } else {
        setAssignments([{ userId: job.assignedTo as string, role: 'Team Member', price: null, payType: 'job' as const, hourlyRate: null }])
      }
    } else if (defaultAssignedTo && defaultAssignedTo.length > 0) {
      // Use defaultAssignedTo if no job is being edited
      setAssignments(defaultAssignedTo)
    } else {
      setAssignments([])
    }
  }, [job, defaultAssignedTo])

  // Match existing roles to job roles when jobRoles are loaded
  useEffect(() => {
    if (jobRoles.length > 0 && assignments.length > 0 && job) {
      const updatedAssignments = assignments.map(assignment => {
        // If roleId is already set and valid, keep it
        if (assignment.roleId && assignment.roleId !== 'custom' && jobRoles.some(r => r.id === assignment.roleId)) {
          return assignment
        }
        
        // If role exists but no roleId or invalid roleId, try to match it to a job role
        if (assignment.role) {
          const matchingRole = jobRoles.find(r => r.title === assignment.role)
          if (matchingRole) {
            // Found a match - set the roleId
            return {
              ...assignment,
              roleId: matchingRole.id,
            }
          } else {
            // No match found - this is a custom role, set roleId to 'custom'
            return {
              ...assignment,
              roleId: 'custom',
            }
          }
        }
        
        // No role set - keep as is
        return assignment
      })
      
      // Only update if something changed to avoid infinite loops
      const hasChanges = updatedAssignments.some((updated, index) => 
        updated.roleId !== assignments[index]?.roleId
      )
      
      if (hasChanges) {
        setAssignments(updatedAssignments)
        setValue('assignedTo', updatedAssignments)
      }
    }
  }, [jobRoles, job?.id, assignments, setValue]) // Include assignments and setValue to avoid stale closures

  useEffect(() => {
    if (job) {
      reset({
        title: job.title,
        description: job.description || '',
        contactId: job.contactId,
        serviceId: job.serviceId || '',
        // API can return null for unscheduled jobs; schema expects string (or empty string).
        startTime: job.startTime ?? '',
        endTime: job.endTime ?? '',
        status: job.status || 'active',
        location: job.location || '',
        price: job.price != null ? job.price.toString() : '',
        notes: job.notes || '',
        assignedTo: (() => {
          if (!job.assignedTo) return []
          if (Array.isArray(job.assignedTo)) {
            if (
              job.assignedTo.length > 0 &&
              typeof job.assignedTo[0] === 'object' &&
              'userId' in job.assignedTo[0]
            ) {
              // Ensure payType and hourlyRate are set for all assignments
              return (job.assignedTo as JobAssignment[]).map(assignment => ({
                ...assignment,
                payType: assignment.payType || 'job',
                hourlyRate: assignment.hourlyRate ?? null,
                price: assignment.price ?? null,
              }))
            }
            return (job.assignedTo as string[]).map(id => ({
              userId: id,
              role: 'Team Member',
              price: null,
              payType: 'job' as const,
              hourlyRate: null,
            }))
          }
          return [{ userId: job.assignedTo as string, role: 'Team Member', price: null, payType: 'job' as const, hourlyRate: null }]
        })(),
      })
      if (job.startTime && job.endTime) {
        setStartDate(format(new Date(job.startTime), 'yyyy-MM-dd'))
        setStartTime(format(new Date(job.startTime), 'HH:mm'))
        const inferred = inferDurationUnit(job.startTime, job.endTime)
        setDurationUnit(inferred.unit)
        setDurationValue(inferred.value)
        setScheduleDateError(null)
      }
      if (!job.startTime || !job.endTime) {
        // Ensure we don't keep stale date/time state when switching between jobs
        setStartDate('')
        setStartTime('09:00')
        setScheduleDateError(null)
      }
      // Staged monthly (to-be-scheduled + recurrence) is a rolling placeholder — keep the
      // form in placeholder mode so the "Every month" control shows and the series can be
      // edited/turned off. Otherwise, scheduling an unscheduled job unchecks toBeScheduled.
      const isStagedMonthlyJob = !!(job.toBeScheduled && job.recurrenceId)
      const shouldBeScheduled =
        !canSchedule ||
        (isStagedMonthlyJob ? true : schedulingUnscheduledJob ? false : job.toBeScheduled || false)
      setToBeScheduled(shouldBeScheduled)
      if (isStagedMonthlyJob) {
        setRepeatPattern('monthly-1')
      }
    }
  }, [job, reset, schedulingUnscheduledJob])

  // Update form when existing job is selected (must be after useForm hook)
  useEffect(() => {
    if (selectedExistingJobId && jobSelectionMode === 'existing' && !job) {
      const selectedJob = filteredJobs.find(j => j.id === selectedExistingJobId)
      if (selectedJob) {
        // Pre-fill form with selected job's data
        reset({
          title: selectedJob.title,
          description: selectedJob.description,
          contactId: selectedJob.contactId,
          serviceId: selectedJob.serviceId || '', // Convert null to empty string for form
          quoteId: selectedJob.quoteId || '',
          invoiceId: selectedJob.invoiceId || '',
          location: selectedJob.location || '',
          price: selectedJob.price?.toString() || '',
          notes: selectedJob.notes || '',
          assignedTo: Array.isArray(selectedJob.assignedTo) 
            ? selectedJob.assignedTo.filter((a: any) => a?.userId)
            : [],
        })
        if (selectedJob.startTime && selectedJob.endTime) {
          setStartDate(format(new Date(selectedJob.startTime), 'yyyy-MM-dd'))
          setStartTime(format(new Date(selectedJob.startTime), 'HH:mm'))
          const inferred = inferDurationUnit(selectedJob.startTime, selectedJob.endTime)
          setDurationUnit(inferred.unit)
          setDurationValue(inferred.value)
        }
      }
    }
  }, [selectedExistingJobId, jobSelectionMode, filteredJobs, reset, job])

  const handleFormSubmit = async (data: JobFormData) => {
    // Clear any previous submit-level error (invalid form, etc.)
    if (submitError) setSubmitError(null)

    if (canShowAssignee) {
      const roleMsg = getTeamAssignmentRoleValidationMessage(assignments, jobRoles)
      if (roleMsg) {
        setSubmitError(roleMsg)
        return
      }
    }

    // Helper function to safely convert price to number
    const convertPrice = (price: any): number | undefined => {
      if (price === undefined || price === null || price === '') {
        return undefined
      }
      const numPrice = typeof price === 'string' ? parseFloat(price) : price
      return !isNaN(numPrice) ? numPrice : undefined
    }

    // If user doesn't have permission to see job prices, don't send job price
    // But they can still see their own assignment pay
    const shouldIncludeJobPrice = canSeeJobPrices

    const normalizedAssignments =
      Array.isArray(data.assignedTo) && data.assignedTo.length > 0
        ? data.assignedTo
            .filter(a => a?.userId && a.userId.trim() !== '')
            .map(a => {
              // Convert roleId: 'custom' to undefined before submission
              const normalizedA = {
                ...a,
                roleId: a.roleId === 'custom' ? undefined : a.roleId,
              }
              // If roleId is missing but role title matches an existing JobRole, attach roleId.
              // This prevents "legacy/self-only" behavior when someone typed a role name.
              if (!normalizedA.roleId && normalizedA.role && jobRoles.length > 0) {
                const match = jobRoles.find(r => r.title === normalizedA.role)
                if (match) {
                  normalizedA.roleId = match.id
                }
              }
              // Employees can see their own assignment pay, but not others'
              // If user doesn't have permission to see job prices, they can't edit assignment prices
              // But they can still see their own assignment pay (read-only, handled in UI)
              if (!canSeeJobPrices) {
                if (normalizedA.userId === currentUserId) {
                  // Keep their own assignment but don't allow price changes
                  // Preserve existing price from the job if editing, otherwise null
                  const existingAssignment = job?.assignedTo && Array.isArray(job.assignedTo) 
                    ? (job.assignedTo as JobAssignment[]).find(existing => existing.userId === normalizedA.userId)
                    : null
                  return {
                    ...normalizedA,
                    // Preserve existing price/hourlyRate if editing, otherwise keep what's in form (but it's disabled)
                    price: existingAssignment?.price ?? normalizedA.price,
                    hourlyRate: existingAssignment?.hourlyRate ?? normalizedA.hourlyRate,
                  }
                } else {
                  // No permission to see coworkers' pay — never overwrite it either. Preserve
                  // whatever the job already stores (the form never displayed it); the backend
                  // additionally restores stored pay server-side for editors without
                  // canSeeJobPrices, so a notes-only edit can't wipe a coworker's rate.
                  const existingAssignment = job?.assignedTo && Array.isArray(job.assignedTo)
                    ? (job.assignedTo as JobAssignment[]).find(existing => existing.userId === normalizedA.userId)
                    : null
                  return {
                    ...normalizedA,
                    price: existingAssignment?.price ?? null,
                    hourlyRate: existingAssignment?.hourlyRate ?? null,
                  }
                }
              }
              return normalizedA
            })
        : null

    // If toBeScheduled, skip date/time validation and send without times
    if (toBeScheduled) {
      const { startTime: _st, endTime: _et, ...dataWithoutTimes } = data
      const formData: any = {
        ...dataWithoutTimes,
        toBeScheduled: true,
        breaks: undefined,
        // Convert empty strings to undefined for foreign keys
        quoteId: (dataWithoutTimes.quoteId && dataWithoutTimes.quoteId !== '') ? dataWithoutTimes.quoteId : undefined,
        invoiceId: (dataWithoutTimes.invoiceId && dataWithoutTimes.invoiceId !== '') ? dataWithoutTimes.invoiceId : undefined,
        serviceId: (dataWithoutTimes.serviceId && dataWithoutTimes.serviceId !== '') ? dataWithoutTimes.serviceId : undefined,
        assignedTo: normalizedAssignments,
        // Convert price string to number, or undefined if empty
        // Don't include job price if user doesn't have permission
        price: shouldIncludeJobPrice ? convertPrice(dataWithoutTimes.price) : undefined,
      }
      // Staged monthly: a to-be-scheduled job set to "Every month" becomes a rolling
      // placeholder. Attach the monthly recurrence; if an existing staged job is switched
      // back to "Does not repeat", signal the backend to stop the rolling.
      const wantsStagedMonthly =
        repeatPattern === 'monthly-1' && jobSelectionMode !== 'independent'
      const isEditingStagedMonthly = !!(job?.toBeScheduled && job?.recurrenceId)
      if (wantsStagedMonthly) {
        formData.recurrence = { frequency: 'monthly', interval: 1 }
      } else if (isEditingStagedMonthly) {
        formData.removeRecurrence = true
      }
      const existingJobId = jobSelectionMode === 'existing' ? selectedExistingJobId : undefined
      const isIndependent = jobSelectionMode === 'independent'
      const needsEffectiveDate =
        !isIndependent &&
        job &&
        propTimeEntries &&
        canSeeJobPrices &&
        hasPayChangeWithTimeEntries(job.assignedTo, normalizedAssignments, propTimeEntries)
      const { _jobSelectionMode: _, ...cleanFormData } = formData as any
      if (needsEffectiveDate) {
        setPendingSubmit({ formData: cleanFormData, existingJobId, isIndependent })
        setShowPayChangeModal(true)
        return
      }
      await onSubmit(cleanFormData, existingJobId, isIndependent)
      return
    }

    // Validate date is selected
    if (!startDate) {
      // This used to be a silent no-op (felt like the button "did nothing")
      setScheduleDateError('Please select a date.')
      return
    }

    // Compute start and end times based on duration unit
    let startDateTime: Date
    let endDateTime: Date

    if (isAllDay) {
      // All-day event: starts at 00:00 and ends at 23:59
      startDateTime = new Date(`${startDate}T00:00:00`)
      const totalDays = durationUnit === 'days' ? durationValue : durationValue * 7
      // Advance by CALENDAR days, not (totalDays-1)*24h. Across the November fall-back a local day
      // is 25h, so ms arithmetic lands an hour before local midnight — i.e. the previous calendar
      // day — and the event silently loses its last day. setDate handles variable-length days.
      endDateTime = new Date(startDateTime)
      endDateTime.setDate(endDateTime.getDate() + (totalDays - 1))
      // Set to end of last day (23:59:59)
      endDateTime.setHours(23, 59, 59, 999)
    } else {
      // Use provided time or default to 9:00 AM if not specified
      const effectiveStartTime = startTime || '09:00'

      if (durationUnit === 'minutes') {
        // Use specific time for minute-based jobs
        startDateTime = new Date(`${startDate}T${effectiveStartTime}`)
        endDateTime = new Date(startDateTime.getTime() + durationValue * 60000)
      } else if (durationUnit === 'hours') {
        // Use specific time for hour-based jobs
        startDateTime = new Date(`${startDate}T${effectiveStartTime}`)
        endDateTime = new Date(startDateTime.getTime() + durationValue * 60 * 60000)
      } else if (
        // Editing round-trip: when the date and the inferred duration are untouched, keep the
        // job's real times verbatim. A job spanning 24h+ with specific clock times (e.g. Mon
        // 8am → Tue 10am) is shown in the form as "2 days", and normalizing it to 9:00–23:59
        // below would silently rewrite its schedule on an unrelated resave.
        job &&
        job.startTime &&
        job.endTime &&
        startDate === format(new Date(job.startTime), 'yyyy-MM-dd') &&
        durationUnit === initialDuration.unit &&
        durationValue === initialDuration.value
      ) {
        startDateTime = new Date(job.startTime)
        endDateTime = new Date(job.endTime)
      } else {
        // For day/week jobs, use a fixed time (9 AM) and compute end date
        // N days = start of day 1 through end of day N (inclusive)
        startDateTime = new Date(`${startDate}T09:00:00`)
        const totalDays = durationUnit === 'days' ? durationValue : durationValue * 7
        // Advance by CALENDAR days (see the all-day branch): ms arithmetic loses the last day
        // across the November DST fall-back.
        endDateTime = new Date(startDateTime)
        endDateTime.setDate(endDateTime.getDate() + (totalDays - 1))
        endDateTime.setHours(23, 59, 59, 999)
      }
    }

    const formData: any = {
      ...data,
      startTime: startDateTime.toISOString(),
      endTime: endDateTime.toISOString(),
      toBeScheduled: false,
      breaks: breaks.length > 0 ? breaks : undefined,
      // Convert empty strings and null to undefined for foreign keys
      quoteId: (data.quoteId && data.quoteId !== '') ? data.quoteId : undefined,
      invoiceId: (data.invoiceId && data.invoiceId !== '') ? data.invoiceId : undefined,
      serviceId: (data.serviceId && data.serviceId !== '') ? data.serviceId : undefined,
      assignedTo: normalizedAssignments,
      // Convert price string to number, or undefined if empty
      // Don't include job price if user doesn't have permission
      price: shouldIncludeJobPrice ? convertPrice(data.price) : undefined,
    }

    // Add recurrence if selected
    console.log('🔄 JobForm: Checking recurrence', {
      repeatPattern,
      isNone: repeatPattern === 'none',
      job: job ? 'editing' : 'creating',
      jobId: job?.id,
    })

    if (repeatPattern !== 'none' && jobSelectionMode !== 'independent') {
      const [frequency, intervalStr] = repeatPattern.split('-') as [RecurrenceFrequency, string]
      const interval = parseInt(intervalStr) || 1

      console.log('➕ JobForm: Adding recurrence', { frequency, interval, endRepeatMode })

      // Validate custom recurrence has days selected
      if (frequency === 'custom' && customDays.length === 0) {
        alert('Please select at least one day for custom recurrence')
        return
      }

      if (endRepeatMode === 'never') {
        // No count and no untilDate is what marks a series open-ended server-side.
        formData.recurrence = {
          frequency,
          interval,
          daysOfWeek: frequency === 'custom' ? customDays : undefined,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }
        console.log('✅ JobForm: Recurrence added (repeats indefinitely)', formData.recurrence)
      } else if (endRepeatMode === 'after') {
        formData.recurrence = {
          frequency,
          interval,
          count: occurrenceCount,
          daysOfWeek: frequency === 'custom' ? customDays : undefined,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }
        console.log('✅ JobForm: Recurrence added (fixed count)', formData.recurrence)
      } else if (endRepeatMode === 'on-date' && endRepeatDate) {
        // Calculate count based on end date
        const start = new Date(`${startDate}T${startTime || '09:00'}`)
        // Parse the end date as LOCAL end-of-day. `new Date('YYYY-MM-DD')` is UTC midnight, which
        // for negative-offset (US) users is earlier than the intended local end-of-day, so an
        // occurrence landing ON the end date fails `<= end` and the count comes up one short.
        const end = new Date(`${endRepeatDate}T23:59:59`)
        let count = 1
        let currentDate = new Date(start)

        if (frequency === 'custom') {
          // For custom, count occurrences on selected days
          currentDate.setDate(currentDate.getDate() + 1) // Start from next day
          while (currentDate <= end) {
            if (customDays.includes(currentDate.getDay())) {
              count++
            }
            currentDate.setDate(currentDate.getDate() + 1)
          }
        } else {
          while (currentDate <= end) {
            if (frequency === 'daily') {
              currentDate.setDate(currentDate.getDate() + interval)
            } else if (frequency === 'weekly') {
              currentDate = addWeeks(currentDate, interval)
            } else if (frequency === 'monthly') {
              currentDate = addMonths(currentDate, interval)
            }
            if (currentDate <= end) count++
          }
        }

        formData.recurrence = {
          frequency,
          interval,
          count: count,
          daysOfWeek: frequency === 'custom' ? customDays : undefined,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }
        console.log('✅ JobForm: Recurrence added (ends on date)', formData.recurrence)
      }
    } else {
      console.log('⏭️ JobForm: No recurrence selected, skipping')
    }

    console.log('📤 JobForm: Final formData being submitted:', {
      ...formData,
      recurrence: formData.recurrence ? 'yes' : 'no',
      recurrenceDetails: formData.recurrence,
    })
    const existingJobId = jobSelectionMode === 'existing' ? selectedExistingJobId : undefined
    const isIndependent = jobSelectionMode === 'independent'
    const needsEffectiveDate =
      !isIndependent &&
      job &&
      propTimeEntries &&
      canSeeJobPrices &&
      hasPayChangeWithTimeEntries(job.assignedTo, normalizedAssignments, propTimeEntries)
    const { _jobSelectionMode: _, ...cleanFormData } = formData as any
    if (needsEffectiveDate) {
      setPendingSubmit({ formData: cleanFormData, existingJobId, isIndependent })
      setShowPayChangeModal(true)
      return
    }
    await onSubmit(cleanFormData, existingJobId, isIndependent)
  }

  const handlePayChangeModalConfirm = async (effectiveDate: string) => {
    if (!pendingSubmit) return
    const { formData, existingJobId, isIndependent } = pendingSubmit
    setShowPayChangeModal(false)
    setPendingSubmit(null)
    await onSubmit({ ...formData, payChangeEffectiveDate: effectiveDate }, existingJobId, isIndependent)
  }

  const handleInvalidSubmit = (invalid: any) => {
    // React Hook Form will not call handleFormSubmit when invalid; without this it can feel like "nothing happened".
    // Surface the actual failing fields, even if the UI doesn't highlight them (e.g. nested assignedTo.* paths).
    const messages: string[] = []
    const walk = (obj: any, path: string) => {
      if (!obj) return
      if (typeof obj.message === 'string') {
        messages.push(path ? `${path}: ${obj.message}` : obj.message)
      }
      if (typeof obj === 'object') {
        for (const key of Object.keys(obj)) {
          if (key === 'message' || key === 'type' || key === 'ref') continue
          walk(obj[key], path ? `${path}.${key}` : key)
        }
      }
    }
    walk(invalid, '')

    setSubmitError(
      messages.length > 0
        ? `Fix these fields: ${messages.slice(0, 3).join(' | ')}`
        : 'Form is invalid. Please review the fields and try again.'
    )
  }

  // Calculate end date for recurrence preview
  const getRecurrenceEndDate = () => {
    if (!startDate || !startTime || repeatPattern === 'none') return null

    const start = new Date(`${startDate}T${startTime}`)
    const [frequency, intervalStr] = repeatPattern.split('-')
    const interval = parseInt(intervalStr) || 1

    let endDate = new Date(start)
    const count = occurrenceCount - 1 // -1 because first occurrence is the start date

    if (frequency === 'custom') {
      // For custom, estimate based on average days per week
      if (customDays.length === 0) return null
      const daysPerWeek = customDays.length
      const weeksNeeded = Math.ceil(count / daysPerWeek)
      endDate.setDate(endDate.getDate() + weeksNeeded * 7)
    } else if (frequency === 'daily') {
      endDate.setDate(endDate.getDate() + interval * count)
    } else if (frequency === 'weekly') {
      endDate = addWeeks(start, interval * count)
    } else if (frequency === 'monthly') {
      endDate = addMonths(start, interval * count)
    }

    return format(endDate, 'MMM d, yyyy')
  }

  // Helper function to extract last name from contact name
  const getLastName = (contactName?: string): string => {
    if (!contactName) return ''
    const parts = contactName.trim().split(/\s+/)
    return parts.length > 0 ? parts[parts.length - 1] : contactName
  }

  // Prepare job title options - include services, quotes, and invoices
  const approvedQuotes = quotes.filter(
    q => q.status === 'draft' || q.status === 'sent' || q.status === 'accepted'
  )
  const approvedInvoices = invoices.filter(
    i => i.status === 'draft' || i.status === 'sent' || i.approvalStatus === 'accepted'
  )
  const activeServices = services.filter(s => s.isActive)

  // Title is always a text field - dropdown removed

  // Determine if we should show simplified form (simple create mode and not editing)
  const showSimplifiedForm = isSimpleCreate && !job

  return (
    <form onSubmit={handleSubmit(handleFormSubmit, handleInvalidSubmit)} className="space-y-5 sm:space-y-4">
      {/* Job Selection Mode - Show when allowLinkExistingJob is true and not editing */}
      {allowLinkExistingJob && !job && !schedulingUnscheduledJob && canSeeOtherJobs && (
        <div className="space-y-4">
          <div className="flex gap-4">
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="radio"
                name="jobSelectionMode"
                checked={jobSelectionMode === 'new'}
                onChange={() => {
                  setJobSelectionMode('new')
                  setSelectedExistingJobId(undefined)
                  setValue('_jobSelectionMode', 'new')
                }}
                className="h-4 w-4 accent-accent-strong focus-visible:ring-accent"
              />
              <span className="text-sm text-ink">
                <span className="sm:hidden">New Job</span>
                <span className="hidden sm:inline">Create New Job</span>
              </span>
            </label>
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="radio"
                name="jobSelectionMode"
                checked={jobSelectionMode === 'existing'}
                onChange={() => {
                  setJobSelectionMode('existing')
                  setValue('_jobSelectionMode', 'existing')
                }}
                className="h-4 w-4 accent-accent-strong focus-visible:ring-accent"
              />
              <span className="text-sm text-ink">
                <span className="sm:hidden">Existing Job</span>
                <span className="hidden sm:inline">Link to Existing Job</span>
              </span>
            </label>
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="radio"
                name="jobSelectionMode"
                checked={jobSelectionMode === 'independent'}
                onChange={() => {
                  setJobSelectionMode('independent')
                  setSelectedExistingJobId(undefined)
                  setValue('_jobSelectionMode', 'independent')
                }}
                className="h-4 w-4 accent-accent-strong focus-visible:ring-accent"
              />
              <span className="text-sm text-ink">
                <span className="sm:hidden">Independent</span>
                <span className="hidden sm:inline">Independent Appointment</span>
              </span>
            </label>
          </div>

          {jobSelectionMode === 'existing' && (
            <SearchableSelectField
              label="Select Job"
              value={selectedExistingJobId || ''}
              onChange={value => setSelectedExistingJobId(value || undefined)}
              placeholder="Search and select a job..."
              searchPlaceholder="Search by title, contact, or service..."
              options={[
                { value: '', label: 'Select a job...' },
                ...filteredJobs.map(j => ({
                  value: j.id,
                  label: `${j.title}${j.contactName ? ` - ${j.contactName}` : ''}${j.serviceName ? ` (${j.serviceName})` : ''}`,
                })),
              ]}
            />
          )}
        </div>
      )}

      {/* Job Title - Always a text field */}
      <TextField
        label="Job Title *"
        {...register('title')}
        error={errors.title?.message}
        placeholder="e.g., Weekly Clean, Move-Out Clean"
      />

      <TextAreaField
        label="Description"
        {...register('description')}
        rows={3}
        placeholder="Add job description..."
      />

      <Controller
        name="contactId"
        control={control}
        render={({ field }) => (
          <SearchableSelectField
            label={jobSelectionMode === 'independent' ? 'Contact (optional)' : 'Contact *'}
            placeholder="Select a contact"
            searchPlaceholder="Search by name or company..."
            value={field.value}
            onChange={field.onChange}
            error={errors.contactId?.message}
            options={[
              { value: '', label: 'Select a contact' },
              ...contacts.map(contact => ({
                value: contact.id,
                label: `${contact.firstName} ${contact.lastName}${contact.company ? ` - ${contact.company}` : ''}`,
              })),
            ]}
          />
        )}
      />

      {/* Location field - shown in both simplified and full forms */}
      {showSimplifiedForm ? (
        <TextField
          label="Location"
          {...register('location')}
          error={errors.location?.message}
          placeholder="e.g., 123 Main St, New York, NY"
        />
      ) : null}

      {/* All other fields - only show when NOT in simplified create mode */}
      {!showSimplifiedForm && (
        <>
      {canShowAssignee && (
        <div className="pt-2">
          <label className={labelCls}>
            Assign to Team Members (with Roles &amp; Pricing)
          </label>
          <p className="mb-4 text-[13px] leading-relaxed text-ink-subtle">
            Assign team members to this job and set their role and individual pricing. Team members
            can only see their own pricing.
          </p>
          <div className="space-y-3">
            {assignments.length === 0 ? (
              <div className="rounded-xl border border-line bg-surface-2 p-4 text-center">
                <p className="mb-3 text-sm text-ink-muted">No team members assigned yet</p>
                <AppButton
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const newAssignments = [{ userId: '', role: undefined, roleId: undefined, price: null, payType: 'job' as const, hourlyRate: null }]
                    setAssignments(newAssignments)
                    setValue('assignedTo', newAssignments)
                  }}
                >
                  <PlusIcon className="h-4 w-4" />
                  Add Team Member
                </AppButton>
              </div>
            ) : (
              <>
                {assignments.map((assignment, index) => {
                  const member = teamMembers.find(m => m.id === assignment.userId)
                  return (
                    <div
                      key={index}
                      className="space-y-3 rounded-xl border border-line bg-surface-2 p-3"
                    >
                      <div className="flex flex-col items-start gap-3 sm:flex-row">
                        <div className="grid w-full flex-1 grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                          <div>
                            <label className="mb-1 block text-xs font-medium text-ink-muted">
                              Team Member
                            </label>
                            <SelectField
                              aria-label="Team Member"
                              value={assignment.userId}
                              onChange={e => {
                                const newAssignments = [...assignments]
                                newAssignments[index] = { ...assignment, userId: e.target.value }
                                setAssignments(newAssignments)
                                setValue('assignedTo', newAssignments)
                              }}
                              options={[
                                { value: '', label: 'Select member' },
                                ...teamMembers.map(m => ({ value: m.id, label: m.name })),
                              ]}
                            />
                          </div>
                          <div>
                            <label className="mb-1 block text-xs font-medium text-ink-muted">
                              Role
                            </label>
                            {jobRoles.length > 0 ? (
                              <SelectField
                                aria-label="Role"
                                value={assignment.roleId || ''}
                                placeholder="Select role"
                                onChange={e => {
                                  const newAssignments = [...assignments]
                                  const selectedRoleId = e.target.value
                                  if (selectedRoleId === '') {
                                    // No role selected - clear roleId and role
                                    newAssignments[index] = { ...assignment, roleId: undefined, role: undefined }
                                  } else if (selectedRoleId === 'custom') {
                                    // Custom role - set roleId to 'custom' to track that custom was selected, set role to empty string
                                    newAssignments[index] = { ...assignment, roleId: 'custom', role: '' }
                                  } else {
                                    // Selected role from list
                                    const selectedRole = jobRoles.find(r => r.id === selectedRoleId)
                                    newAssignments[index] = {
                                      ...assignment,
                                      roleId: selectedRoleId,
                                      role: selectedRole?.title || assignment.role || '',
                                    }
                                  }
                                  setAssignments(newAssignments)
                                  setValue('assignedTo', newAssignments)
                                }}
                                options={[
                                  { value: '', label: 'Select role' },
                                  { value: 'custom', label: 'Custom...' },
                                  ...jobRoles.map(r => ({ value: r.id, label: r.title })),
                                ]}
                              />
                            ) : null}
                            {(jobRoles.length === 0 || assignment.roleId === 'custom') && (
                              <TextField
                                type="text"
                                value={assignment.role || ''}
                                onChange={e => {
                                  const newAssignments = [...assignments]
                                  // Keep roleId as 'custom' when typing to prevent input from unmounting
                                  newAssignments[index] = { ...assignment, role: e.target.value, roleId: 'custom' }
                                  setAssignments(newAssignments)
                                  setValue('assignedTo', newAssignments)
                                }}
                                placeholder="e.g., Lead, Assistant"
                                className="mt-1"
                              />
                            )}
                          </div>
                          <div>
                            <label className="mb-1 block text-xs font-medium text-ink-muted">
                              Pay Type
                            </label>
                            <SelectField
                              aria-label="Pay Type"
                              value={assignment.payType || 'job'}
                              onChange={e => {
                                const newAssignments = [...assignments]
                                const payType = e.target.value as 'job' | 'hourly'
                                newAssignments[index] = {
                                  ...assignment,
                                  payType,
                                  // Clear price/hourlyRate when switching
                                  price: payType === 'job' ? assignment.price : null,
                                  hourlyRate: payType === 'hourly' ? assignment.hourlyRate : null,
                                }
                                setAssignments(newAssignments)
                                setValue('assignedTo', newAssignments)
                              }}
                              options={[
                                { value: 'job', label: 'By Job' },
                                { value: 'hourly', label: 'By Hour' },
                              ]}
                            />
                          </div>
                          {assignment.payType === 'hourly' ? (
                            <div>
                              <label className="mb-1 block text-xs font-medium text-ink-muted">
                                Hourly Rate
                              </label>
                              <div className="relative">
                                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-ink-subtle">
                                  $
                                </span>
                                <TextField
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={(!canSeeJobPrices && assignment.userId !== currentUserId) ? '' : (assignment.hourlyRate ?? '')}
                                  onChange={e => {
                                    const newAssignments = [...assignments]
                                    const hourlyRateValue =
                                      e.target.value === '' ? null : parseFloat(e.target.value)
                                    newAssignments[index] = {
                                      ...assignment,
                                      hourlyRate: isNaN(hourlyRateValue as number) ? null : hourlyRateValue,
                                    }
                                    setAssignments(newAssignments)
                                    setValue('assignedTo', newAssignments)
                                  }}
                                  placeholder="0.00"
                                  className="pl-7 font-mono tabular-nums"
                                  disabled={!canSeeJobPrices || (user?.role === 'employee' && assignment.userId !== currentUserId)}
                                />
                              </div>
                              {(!canSeeJobPrices || (user?.role === 'employee' && assignment.userId !== currentUserId)) && (
                                <p className="mt-0.5 text-xs text-warning">
                                  {assignment.userId === currentUserId ? 'Read-only: Your pay' : 'Insufficient permissions'}
                                </p>
                              )}
                            </div>
                          ) : (
                            <div>
                            <label className="mb-1 block text-xs font-medium text-ink-muted">
                              Price
                            </label>
                              <div className="relative">
                                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-ink-subtle">
                                  $
                                </span>
                                <TextField
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={(!canSeeJobPrices && assignment.userId !== currentUserId) ? '' : (assignment.price ?? '')}
                                  onChange={e => {
                                    const newAssignments = [...assignments]
                                    const priceValue =
                                      e.target.value === '' ? null : parseFloat(e.target.value)
                                    newAssignments[index] = {
                                      ...assignment,
                                      price: isNaN(priceValue as number) ? null : priceValue,
                                    }
                                    setAssignments(newAssignments)
                                    setValue('assignedTo', newAssignments)
                                  }}
                                  placeholder="0.00"
                                  className="pl-7 font-mono tabular-nums"
                                  disabled={!canSeeJobPrices || (user?.role === 'employee' && assignment.userId !== currentUserId)}
                                />
                              </div>
                              {(!canSeeJobPrices || (user?.role === 'employee' && assignment.userId !== currentUserId)) && (
                                <p className="mt-0.5 text-xs text-warning">
                                  {assignment.userId === currentUserId ? 'Read-only: Your pay' : 'Insufficient permissions'}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                        <AppButton
                          type="button"
                          variant="dangerGhost"
                          size="sm"
                          onClick={() => {
                            const newAssignments = assignments.filter((_, i) => i !== index)
                            setAssignments(newAssignments)
                            // Allow empty assignments - set to empty array or undefined
                            if (newAssignments.length === 0) {
                              setValue('assignedTo', undefined)
                            } else {
                              setValue('assignedTo', newAssignments)
                            }
                          }}
                          className="mt-3 sm:mt-0 sm:self-start"
                        >
                          <TrashIcon className="h-4 w-4" />
                          Remove
                        </AppButton>
                      </div>
                    </div>
                  )
                })}
                <AppButton
                  type="button"
                  variant="subtle"
                  size="sm"
                  fullWidth
                  onClick={() => {
                    const newAssignments = [
                      ...assignments,
                      { userId: '', role: undefined, roleId: undefined, price: null, payType: 'job' as const, hourlyRate: null },
                    ]
                    setAssignments(newAssignments)
                    setValue('assignedTo', newAssignments)
                  }}
                >
                  <PlusIcon className="h-4 w-4" />
                  Add Another Team Member
                </AppButton>
              </>
            )}
          </div>
          {errors.assignedTo && (
            <p className="mt-1.5 text-[13px] text-danger">{errors.assignedTo.message}</p>
          )}
        </div>
      )}

      {/* Service field is now integrated into Job Title dropdown above */}

      {/* To Be Scheduled checkbox - show if user can schedule appointments */}
      {canSchedule && (
        <div className="flex items-center gap-3 rounded-lg border border-info/30 bg-info-soft p-3">
          <CheckboxField
            id="toBeScheduled"
            checked={toBeScheduled}
            onChange={checked => {
              setToBeScheduled(checked)
              // Reset the staged repeat control in both directions so a stale "Every month"
              // never leaks into the scheduled path (and vice versa).
              setRepeatPattern('none')
            }}
            label="To Be Scheduled"
          />
          <span className="ml-auto text-xs text-ink-subtle">(Schedule date/time later)</span>
        </div>
      )}
      {!canSchedule && (
        <Alert tone="warning" icon={<AlertIcon className="h-4 w-4" />}>
          You do not have permission to schedule appointments. This job will be created without
          scheduled times.
        </Alert>
      )}

      {/* Staged monthly: repeat control for to-be-scheduled jobs (no fixed day). */}
      {canSchedule && toBeScheduled && jobSelectionMode !== 'independent' && (
        <div>
          <label className={labelCls}>Repeat Schedule</label>
          <SelectField
            aria-label="Repeat Schedule"
            value={repeatPattern === 'monthly-1' ? 'monthly-1' : 'none'}
            onChange={e => setRepeatPattern(e.target.value)}
            options={[
              { value: 'none', label: 'Does not repeat' },
              { value: 'monthly-1', label: 'Every month' },
            ]}
          />
          {repeatPattern === 'monthly-1' && (
            <p className="mt-1.5 text-[13px] text-ink-muted">
              Pins to the top of the calendar each month until you schedule it.
            </p>
          )}
        </div>
      )}

      {/* Job Duration */}
      {!isAllDay && !toBeScheduled && (
        <div>
          <label className={labelCls}>Job Duration *</label>
          <div className="flex items-start gap-2">
            <div className="w-24 shrink-0">
              <TextField
                type="number"
                value={durationValue}
                onChange={e => setDurationValue(Number(e.target.value))}
                min={0.1}
                step={0.1}
                className="font-mono tabular-nums"
              />
            </div>
            <SelectField
              aria-label="Duration unit"
              value={durationUnit}
              onChange={e =>
                handleDurationUnitChange(e.target.value as 'minutes' | 'hours' | 'days' | 'weeks')
              }
              options={[
                { value: 'minutes', label: 'Minutes' },
                { value: 'hours', label: 'Hours' },
                { value: 'days', label: 'Days' },
                { value: 'weeks', label: 'Weeks' },
              ]}
              className="w-32 shrink-0"
            />
          </div>
        </div>
      )}

      {/* Date and Time Selection */}
      {!toBeScheduled && (
        <div className="space-y-3">
          <CheckboxField
            id="isAllDay"
            checked={isAllDay}
            onChange={checked => {
              setIsAllDay(checked)
              if (checked) {
                // Remember timed settings so unchecking can restore them.
                preAllDayDurationRef.current = {
                  unit: durationUnit,
                  value: durationValue,
                  startTime,
                }
                setStartTime('00:00')
                setDurationUnit('days')
                setDurationValue(1)
              } else {
                // Restore the pre-all-day settings so the Start Time field returns.
                const prev = preAllDayDurationRef.current
                setDurationUnit(prev.unit)
                setDurationValue(prev.value)
                setStartTime(prev.startTime)
              }
            }}
            label="All Day Event"
          />

          {!canSchedule && (
            <Alert tone="warning" icon={<AlertIcon className="h-4 w-4" />}>
              You do not have permission to schedule appointments. This job will be created
              without scheduled times.
            </Alert>
          )}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <DateField
              label={
                isAllDay
                  ? 'Start Date *'
                  : durationUnit === 'minutes' || durationUnit === 'hours'
                    ? 'Date *'
                    : 'Start Date *'
              }
              value={startDate}
              onChange={(date) => {
                setStartDate(date)
                if (scheduleDateError) setScheduleDateError(null)
              }}
              disabled={!canSchedule || toBeScheduled}
            />

            {!isAllDay && (durationUnit === 'minutes' || durationUnit === 'hours') && (
              <TimePickerField
                label="Start Time"
                value={startTime}
                onChange={setStartTime}
                placeholder="9:00 AM (default)"
                disabled={!canSchedule || toBeScheduled}
              />
            )}

            {scheduleDateError && (
              <p className="-mt-2 text-[13px] text-danger sm:col-span-2 sm:mt-0">{scheduleDateError}</p>
            )}
          </div>

          {/* End time/date preview */}
          {startDate && (
            <div className="font-mono text-xs tabular-nums text-ink-subtle">
              {isAllDay ? (
                <p>
                  {/* Parse the date-input value as a LOCAL date — new Date('YYYY-MM-DD')
                      is UTC midnight and renders the previous day in US timezones. */}
                  All-day event:{' '}
                  {format(parseDateStringLocal(startDate) ?? new Date(startDate), 'MMM d, yyyy')}
                  {durationValue > 1 && (
                    <>
                      {' '}
                      through{' '}
                      {format(
                        new Date(
                          (parseDateStringLocal(startDate) ?? new Date(startDate)).getTime() +
                            (durationValue - 1) * 24 * 60 * 60 * 1000
                        ),
                        'MMM d, yyyy'
                      )}
                    </>
                  )}
                </p>
              ) : (
                <>
                  {durationUnit === 'minutes' && startTime && (
                    <p>
                      End time:{' '}
                      {format(
                        new Date(`${startDate}T${startTime}`).getTime() + durationValue * 60000,
                        'h:mm a'
                      )}
                    </p>
                  )}
                  {durationUnit === 'hours' && startTime && (
                    <p>
                      End time:{' '}
                      {format(
                        new Date(`${startDate}T${startTime}`).getTime() +
                          durationValue * 60 * 60000,
                        'MMM d, h:mm a'
                      )}
                    </p>
                  )}
                  {(durationUnit === 'days' || durationUnit === 'weeks') && (
                    <p>
                      End date:{' '}
                      {format(
                        new Date(
                          new Date(`${startDate}T09:00:00`).getTime() +
                            ((durationUnit === 'days' ? durationValue : durationValue * 7) - 1) *
                              24 *
                              60 *
                              60 *
                              1000
                        ),
                        'MMM d, yyyy'
                      )}{' '}
                      (All-day job)
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {/*
            Nothing previously stopped you scheduling into the past, and saving still offered to
            email/SMS the client about an appointment that had already happened. Warn at the
            point the date is chosen rather than blocking it outright — backdating a job you
            forgot to log is legitimate, notifying the customer about it is not.
          */}
          {isStartInPast && (
            <p className="text-xs text-warning" role="status">
              This {isAllDay ? 'date is' : 'date and time are'} in the past. The appointment will
              be created as a past booking — skip the client notification unless you mean it.
            </p>
          )}
        </div>
      )}

      <div>
        <label className={labelCls}>Location</label>
        <SelectField
          aria-label="Location source"
          value={locationMode}
          onChange={e => {
            const mode = e.target.value as 'contact' | 'custom'
            setLocationMode(mode)
            if (mode === 'contact') {
              const contactId = watch('contactId')
              const contact = contacts.find(c => c.id === contactId)
              if (contact && contact.address) {
                const fullAddress = [contact.address, contact.city, contact.state, contact.zipCode]
                  .filter(Boolean)
                  .join(', ')
                setValue('location', fullAddress)
              }
            } else {
              setValue('location', customLocation)
            }
          }}
          options={[
            { value: 'contact', label: 'Contact Address' },
            { value: 'custom', label: 'Add Other Address' },
          ]}
        />
        {locationMode === 'custom' && (
          <TextField
            type="text"
            value={customLocation}
            onChange={e => {
              setCustomLocation(e.target.value)
              setValue('location', e.target.value)
            }}
            placeholder="e.g., 123 Main St, New York, NY"
            className="mt-2"
          />
        )}
        {locationMode === 'contact' && watch('contactId') && (
          <p className="mt-2 text-xs text-ink-subtle">
            {(() => {
              const contact = contacts.find(c => c.id === watch('contactId'))
              if (contact && contact.address) {
                return [contact.address, contact.city, contact.state, contact.zipCode]
                  .filter(Boolean)
                  .join(', ')
              }
              return 'No address available for this contact'
            })()}
          </p>
        )}
      </div>

      {/* Price Field */}
      <div>
        <label className={labelCls}>Price</label>
        {canSeeJobPrices ? (
          <>
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-ink-subtle">$</span>
              <TextField
                {...register('price')}
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                className="pl-7 font-mono tabular-nums"
                error={errors.price?.message}
              />
            </div>
            <p className="mt-1 text-xs text-ink-subtle">Optional job price or estimated cost</p>
          </>
        ) : (
          <p className="text-xs text-warning">Insufficient permissions to view or edit prices</p>
        )}
      </div>

      {/* Job Timeline & Breaks */}
      <div className="border-t border-line pt-4">
        <button
          type="button"
          onClick={() => setShowBreaks(!showBreaks)}
          className="mb-2 flex items-center gap-2 text-sm font-medium text-ink"
        >
          <span className="text-accent-strong">{showBreaks ? '▼' : '▶'}</span>
          <span>Job Timeline &amp; Breaks (Optional)</span>
          {breaks.length > 0 && (
            <span className="text-accent-strong">
              ({breaks.length} break{breaks.length !== 1 ? 's' : ''})
            </span>
          )}
        </button>

        {showBreaks && (
          <div className="mt-3 space-y-3">
            <p className="text-xs text-ink-subtle">
              Add planned pauses to the job timeline (e.g., rain delays, material delivery waits)
            </p>

            {breaks.map((breakItem, index) => (
              <div
                key={index}
                className="space-y-3 rounded-xl border border-line bg-surface-2 p-3"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-ink">Break {index + 1}</span>
                  <AppButton
                    type="button"
                    variant="dangerGhost"
                    size="sm"
                    onClick={() => {
                      const newBreaks = breaks.filter((_, i) => i !== index)
                      setBreaks(newBreaks)
                    }}
                  >
                    <TrashIcon className="h-4 w-4" />
                    Remove
                  </AppButton>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-ink-muted">
                      Pause From
                    </label>
                    <DateField
                      value={breakItem.startTime.substring(0, 10)}
                      onChange={date => {
                        const newBreaks = [...breaks]
                        newBreaks[index] = {
                          ...breakItem,
                          startTime: `${date}T00:00:00.000Z`,
                        }
                        setBreaks(newBreaks)
                      }}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-ink-muted">
                      Resume On
                    </label>
                    <DateField
                      value={breakItem.endTime.substring(0, 10)}
                      onChange={date => {
                        const newBreaks = [...breaks]
                        newBreaks[index] = {
                          ...breakItem,
                          endTime: `${date}T00:00:00.000Z`,
                        }
                        setBreaks(newBreaks)
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-1 block text-xs font-medium text-ink-muted">
                    Reason (Optional)
                  </label>
                  <TextField
                    type="text"
                    value={breakItem.reason || ''}
                    onChange={e => {
                      const newBreaks = [...breaks]
                      newBreaks[index] = { ...breakItem, reason: e.target.value }
                      setBreaks(newBreaks)
                    }}
                    placeholder="e.g., Rain delay, material delivery"
                  />
                </div>
              </div>
            ))}

            <AppButton
              type="button"
              variant="subtle"
              size="sm"
              fullWidth
              onClick={() => {
                const now = new Date()
                setBreaks([
                  ...breaks,
                  {
                    id: `temp-${Date.now()}`,
                    startTime: now.toISOString(),
                    endTime: new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString(),
                    reason: '',
                  },
                ])
              }}
            >
              <PlusIcon className="h-4 w-4" />
              Add Break
            </AppButton>
          </div>
        )}
      </div>

      {/* Recurrence Section - hidden when editing an existing recurring job to avoid accidental changes */}
      {!toBeScheduled && (
        <div className="border-t border-line pt-4">
          {job?.recurrenceId ? (
            <div className="rounded-lg border border-info/30 bg-info-soft p-3 text-sm text-info">
              This job is part of a recurring series. Changes to time or duration will apply to all future occurrences when you select &quot;All Future Jobs&quot;.
            </div>
          ) : (
          <div>
          <label className={labelCls}>
            Repeat Schedule
          </label>
          <SelectField
            aria-label="Repeat Schedule"
            value={repeatPattern}
            onChange={e => {
              setRepeatPattern(e.target.value)
            }}
            options={[
              { value: 'none', label: 'Does not repeat' },
              { value: 'daily-1', label: 'Every day' },
              { value: 'daily-2', label: 'Every 2 days' },
              { value: 'weekly-1', label: 'Every week' },
              { value: 'weekly-2', label: 'Every 2 weeks' },
              { value: 'weekly-4', label: 'Every 4 weeks' },
              { value: 'monthly-1', label: 'Every month' },
              { value: 'custom-1', label: 'Custom (select days)' },
            ]}
          />

          {repeatPattern !== 'none' && (
            <div className="mt-3 space-y-3">
              {repeatPattern === 'custom-1' && (
                <div>
                  <label className={labelCls}>
                    Select Days
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { value: 0, label: 'Sun' },
                      { value: 1, label: 'Mon' },
                      { value: 2, label: 'Tue' },
                      { value: 3, label: 'Wed' },
                      { value: 4, label: 'Thu' },
                      { value: 5, label: 'Fri' },
                      { value: 6, label: 'Sat' },
                    ].map(day => (
                      <button
                        key={day.value}
                        type="button"
                        onClick={() => {
                          setCustomDays(prev =>
                            prev.includes(day.value)
                              ? prev.filter(d => d !== day.value)
                              : [...prev, day.value].sort()
                          )
                        }}
                        className={cn(
                          "rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                          customDays.includes(day.value)
                            ? 'bg-accent-strong text-accent-contrast'
                            : 'border border-line bg-surface text-ink hover:border-accent'
                        )}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                  {customDays.length === 0 && (
                    <p className="mt-1 text-xs text-danger">Please select at least one day</p>
                  )}
                </div>
              )}

              <SelectField
                label="End Repeat"
                value={endRepeatMode}
                onChange={e => {
                  const mode = e.target.value as 'never' | 'after' | 'on-date'
                  setEndRepeatMode(mode)
                  if (mode === 'on-date' && !endRepeatDate && startDate) {
                    // Set default end date to 3 months from start. Parse as a LOCAL
                    // date — UTC parsing lands the default one day early for
                    // US-timezone users.
                    const defaultEnd = parseDateStringLocal(startDate) ?? new Date(startDate)
                    defaultEnd.setMonth(defaultEnd.getMonth() + 3)
                    setEndRepeatDate(format(defaultEnd, 'yyyy-MM-dd'))
                  }
                }}
                options={[
                  { value: 'never', label: 'Never' },
                  { value: 'after', label: 'After a number of times' },
                  { value: 'on-date', label: 'On Date' },
                ]}
              />

              {endRepeatMode === 'after' && (
                <TextField
                  label="Number of appointments"
                  type="number"
                  min={2}
                  max={50}
                  value={occurrenceCount}
                  onChange={e => {
                    const next = parseInt(e.target.value, 10)
                    setOccurrenceCount(Number.isNaN(next) ? 2 : Math.min(50, Math.max(2, next)))
                  }}
                  helperText="Between 2 and 50. Choose Never for a series with no end."
                />
              )}

              {endRepeatMode === 'on-date' && (
                <DateField
                  label="End Date"
                  value={endRepeatDate}
                  onChange={setEndRepeatDate}
                  minDate={startDate || new Date().toISOString().split('T')[0]}
                />
              )}

              {endRepeatMode === 'never' && (
                <div className="rounded-lg border border-info/30 bg-info-soft p-3">
                  <p className="text-xs text-info">
                    Repeats with no end date. Appointments are scheduled about a year ahead and
                    extended automatically — delete the job or the series to stop it.
                  </p>
                </div>
              )}

              {endRepeatMode === 'after' && getRecurrenceEndDate() && (
                <div className="rounded-lg border border-info/30 bg-info-soft p-3">
                  <p className="font-mono text-xs tabular-nums text-info">
                    Will create {occurrenceCount} appointments through {getRecurrenceEndDate()}
                  </p>
                </div>
              )}

              {endRepeatMode === 'on-date' && endRepeatDate && (
                <div className="rounded-lg border border-info/30 bg-info-soft p-3">
                  <p className="font-mono text-xs tabular-nums text-info">
                    Will repeat until{' '}
                    {format(parseDateStringLocal(endRepeatDate) ?? new Date(endRepeatDate), 'MMM d, yyyy')}
                  </p>
                </div>
              )}
            </div>
          )}
          </div>
          )}
        </div>
      )}

      <Controller
        name="status"
        control={control}
        render={({ field }) => (
          <SelectField
            label="Status"
            value={field.value || 'scheduled'}
            onChange={field.onChange}
            options={[
              { value: 'scheduled', label: 'Scheduled' },
              { value: 'pending-confirmation', label: 'Unconfirmed' },
              { value: 'in-progress', label: 'In Progress' },
              { value: 'completed', label: 'Completed' },
              { value: 'cancelled', label: 'Cancelled' },
            ]}
          />
        )}
      />

      <TextAreaField
        label="Notes"
        {...register('notes')}
        rows={3}
        placeholder="Add notes..."
      />

        </>
      )}

      {/* Save / Cancel - always shown. Errors render HERE, next to the buttons, so a failed
          submit is visible without scrolling back up the (long) form. */}
      <div className="flex flex-col justify-start gap-3 pt-4 sm:flex-row">
        {(submitError || error) && (
          <div className="w-full sm:mr-auto sm:flex-1">
            <Alert tone="danger" icon={<AlertIcon className="h-4 w-4" />}>
              {submitError || error}
            </Alert>
          </div>
        )}
        <AppButton
          type="button"
          variant="ghost"
          onClick={onCancel}
          disabled={isLoading}
          className="w-full sm:w-auto"
        >
          Cancel
        </AppButton>
        <AppButton type="submit" disabled={isLoading} isLoading={isLoading} className="w-full sm:w-auto">
          {isLoading ? 'Saving...' : job ? 'Update job' : jobSelectionMode === 'independent' ? 'Schedule appointment' : jobSelectionMode === 'existing' ? 'Schedule job' : 'Create job'}
        </AppButton>
      </div>

      <PayChangeEffectiveDateModal
        isOpen={showPayChangeModal}
        onClose={() => {
          setShowPayChangeModal(false)
          setPendingSubmit(null)
        }}
        onConfirm={handlePayChangeModalConfirm}
        isLoading={isLoading}
      />
    </form>
  )
}

export default JobForm
